<template>
  <div class="col-span-6">
    <FormInput
      id="apiToken"
      :secretPreviewEnabled="true"
      :type="showToken.apiToken ? 'text' : 'password'"
      :value="config.apiToken"
      :label="t('company.importer.ubeeo.apiToken')"
      :onInput="(value) => updateToken('apiToken', value)"
      :rules="requiredRule"
      @show-secret="toggleShowToken"
    />
  </div>
  <div class="col-span-6">
    <FormInput
      id="applicantToken"
      :secretPreviewEnabled="true"
      :type="showToken.applicantToken ? 'text' : 'password'"
      :value="config.applicantToken"
      :label="t('company.importer.ubeeo.applicantToken')"
      :onInput="(value) => updateToken('applicantToken', value)"
      :rules="requiredRule"
      @show-secret="toggleShowToken"
    />
  </div>
  <div class="col-span-6">
    <label
      for="jobBoardSites"
      class="flex items-center gap-1 text-sm font-medium text-gray-700"
      >{{ t('company.importer.jobBoardSites')
      }}<HelpPopover :text="t('company.importer.jobBoardSitesHelp')"
    /></label>

    <Multiselect
      id="jobBoardSites"
      ref="multiselect"
      v-model="config.allowedJobBoards"
      :max-height="400"
      :options="searchSite"
      :filterResults="false"
      :noOptionsText="t('vacancy.company.noOptions')"
      :resolveOnLoad="true"
      :minChars="0"
      :delay="500"
      :searchable="true"
      :object="true"
      valueProp="id"
      label="name"
      trackBy="name"
      inputType="search"
      autocomplete="off"
      class="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
      mode="tags"
    />
  </div>
</template>

<script setup>
import { ref, reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { required, helpers } from '@vuelidate/validators'
import FormInput from '../FormInput.vue'
import { useImporterStore } from '../../store/importerStore'
import vacancyImporter from '../../api/vacancyImporter'
import Multiselect from '@vueform/multiselect'
import tooManagement from '../../api/tooManagement'
import HelpPopover from '../HelpPopover.vue'

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  filters: {
    type: Array,
    default: () => [],
  },
  blockedSites: {
    type: Array,
    default: () => [],
  },
})
const emit = defineEmits(['update:modelValue', 'update:filters'])

const importerStore = useImporterStore()

const multiselect = ref(null)

const config = reactive({
  apiToken:
    props.modelValue?.find((prop) => prop.name === 'apiToken')?.value || '',
  applicantToken:
    props.modelValue?.find((prop) => prop.name === 'applicantToken')?.value ||
    '',
  allowedJobBoards: JSON.parse(
    props.modelValue?.find((prop) => prop.name === 'allowedJobBoards')?.value ||
      '[]',
  ),
})

const searchSite = async (filter = '') => {
  const queryRes = await tooManagement.getSites({
    filter: filter || '',
    size: 15,
  })
  return queryRes.data.filter(
    (site) =>
      props.blockedSites.findIndex(
        (blockedSite) => blockedSite.id === site.id,
      ) < 0,
  )
}

watch(config, (value) => {
  emit('update:modelValue', [
    {
      name: 'apiToken',
      value: value.apiToken,
      secret: true,
    },
    {
      name: 'applicantToken',
      value: value.applicantToken,
      secret: true,
    },
    {
      name: 'allowedJobBoards',
      value: JSON.stringify(
        value.allowedJobBoards.map((site) => {
          const siteUrl = new URL(site.siteUrl)
          let domainParts = siteUrl.host.split('.')
          if (domainParts.length > 2) {
            domainParts = domainParts.slice(-2)
          }
          return {
            id: site.id,
            code: site.code,
            name: site.name,
            domain: domainParts.join('.'),
          }
        }),
      ),
      secret: false,
    },
  ])
})

watch(
  () => props.blockedSites,
  () => {
    multiselect.value.refreshOptions?.()
  },
  {
    deep: true,
  },
)

const { t } = useI18n({
  inheritLocale: true,
  useScope: 'global',
})

const requiredRule = {
  required: helpers.withMessage(t('validations.emptyField'), required),
  $autoDirty: true,
}

const showToken = ref({
  apiToken: false,
  applicantToken: false,
})
//once the token value is changed, don't call endpoint to retrieve secret
const tokenInitialValue = ref({
  apiToken: true,
  applicantToken: true,
})

const updateToken = (inputName, value) => {
  if (tokenInitialValue.value[inputName]) {
    tokenInitialValue.value[inputName] = false
  }
  config[inputName] = value
}

const toggleShowToken = async ({ inputName }) => {
  if (
    !showToken.value[inputName] &&
    importerStore.activeConfig.id &&
    tokenInitialValue.value[inputName]
  ) {
    const response = await vacancyImporter.getImportSecret(
      importerStore.activeConfig.id,
      inputName,
    )
    config[inputName] = response?.data?.value || config[inputName]
  }
  showToken.value[inputName] = !showToken.value[inputName]
}
</script>
